import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { TokenSelectSheet } from "@/components/defi/TokenSelectSheet";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { PageHero } from "@/components/ui/PageHero";
import { useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import {
	amountToNat,
	getConfig,
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import type { ExtendedTokenId } from "@/lib/token-assets";
import { formatAmountPlain, parseAmount } from "@/lib/utils";
import {
	approveIcrcSpender,
	approveUsdmForProtocol,
	resolveTransferLedgerId,
	unwrapResult,
} from "@motokomaker/ic-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownUp } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export default function PegSwapPage() {
	const { balances, refreshBalances, ensureProtocolActor } = useAuth();
	const qc = useQueryClient();
	const [from, setFrom] = useState<ExtendedTokenId>("ckUSDC");
	const [to, setTo] = useState<ExtendedTokenId>("USDM");
	const [amount, setAmount] = useState("100");
	const [pending, setPending] = useState(false);

	const statusQ = useQuery({
		queryKey: ["v2", "pegswap", "status"],
		queryFn: async () => {
			const a = await getV2ProtocolActor();
			if (!a || !hasProtocolEnv()) return null;
			const s = await a.getStabilitySwapStatus();
			return {
				enabled: Boolean(s.enabled),
				feeBps: Number(s.feeBps),
				inventory: natToAmount(s.psmInventoryE8),
				cap: natToAmount(s.psmCapE8),
				ledgerWired: Boolean(s.ledgerWired),
				source: "live" as const,
			};
		},
		staleTime: 20_000,
		enabled: hasProtocolEnv(),
	});

	const feeBps = statusQ.data?.feeBps ?? 10;
	const out = useMemo(() => {
		const n = parseAmount(amount);
		return n * (1 - feeBps / 10_000);
	}, [amount, feeBps]);

	const maxBal =
		from === "ckUSDC"
			? (balances.ckUSDC ?? 0)
			: from === "USDM"
				? balances.USDM
				: 0;
	/** Approve + transfer_from each charge 1× ledger fee — reserve 2×. */
	const maxSpendable = depositableTokenMax(maxBal);

	const flip = () => {
		setFrom(to);
		setTo(from);
	};

	async function executeSwap() {
		const n = parseAmount(amount);
		if (!Number.isFinite(n) || n <= 0) {
			toast.error("Enter a valid amount");
			return;
		}
		if (statusQ.data && !statusQ.data.enabled) {
			toast.error("PegSwap is paused on-chain");
			return;
		}
		if (statusQ.data && !statusQ.data.ledgerWired) {
			toast.error("ckUSDC ledger not wired for PegSwap");
			return;
		}
		if (n > maxSpendable + 1e-12) {
			toast.error(
				`Amount exceeds spendable ${from} (wallet minus ledger fees)`,
			);
			return;
		}
		setPending(true);
		try {
			if (!hasProtocolEnv()) {
				toast.error("Connect Internet Identity with protocol env for live PegSwap");
				return;
			}
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Protocol actor not ready — Connect and retry");
				return;
			}
			const config = getConfig();
			const e8 = amountToNat(n);
			const ledgers = await pair.actor.getLedgerCanisters();

			if (from === "ckUSDC" && to === "USDM") {
				const ledgerId = resolveTransferLedgerId(config, "ckUSDC", ledgers);
				if (!ledgerId) throw new Error("ckUSDC ledger not configured in env");
				// ICRC-2 approve protocol as spender (same as v1 useStabilitySwap)
				await approveIcrcSpender(
					pair.agent,
					ledgerId,
					config.protocolCanisterId,
					n,
				);
				unwrapResult(
					await pair.actor.swapCkUsdcForUsdm(e8),
					"swapCkUsdcForUsdm",
				);
			} else if (from === "USDM" && to === "ckUSDC") {
				await approveUsdmForProtocol(pair.agent, config, ledgers, n);
				unwrapResult(
					await pair.actor.swapUsdmForCkUsdc(e8),
					"swapUsdmForCkUsdc",
				);
			} else {
				toast.error("PegSwap supports USDM ↔ ckUSDC only");
				return;
			}
			toast.success(`Swapped ${amount} ${from} → ${out.toFixed(4)} ${to}`);
			void refreshBalances();
			void qc.invalidateQueries({ queryKey: ["v2", "pegswap"] });
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Swap failed");
		} finally {
			setPending(false);
		}
	}

	const st = statusQ.data;

	return (
		<div className="pb-16">
			<PageHero
				eyebrow="PegSwap"
				title="Stability desk"
				subtitle="Fixed-price USDM ↔ ckUSDC on-protocol. Approves the protocol to pull your tokens, then swaps 1:1 minus fee."
			/>
			<div className="action-panel space-y-4">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<p className="text-sm font-semibold">Stability swap</p>
					<span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
						1:1 − {feeBps} bps
						{st
							? st.enabled
								? st.ledgerWired
									? " · live"
									: " · ledger off"
								: " · paused"
							: hasProtocolEnv()
								? " · …"
								: " · offline"}
					</span>
				</div>

				{st ? (
					<div className="grid grid-cols-2 gap-2 rounded-xl border border-border/60 bg-muted/30 p-3 text-[11px] sm:grid-cols-4">
						<div>
							<p className="uppercase text-muted-foreground">Inventory</p>
							<p className="inline-flex items-center gap-1.5 font-mono font-semibold">
								<TokenIcon token="ckUSDC" size="sm" plain />
								{st.inventory.toLocaleString()} ckUSDC
							</p>
						</div>
						<div>
							<p className="uppercase text-muted-foreground">Cap</p>
							<p className="font-mono font-semibold">
								{st.cap.toLocaleString()}
							</p>
						</div>
						<div>
							<p className="uppercase text-muted-foreground">Fee</p>
							<p className="font-mono font-semibold">{st.feeBps} bps</p>
						</div>
						<div>
							<p className="uppercase text-muted-foreground">Status</p>
							<p className="font-semibold">
								{st.enabled ? (st.ledgerWired ? "Open" : "No ledger") : "Paused"}
							</p>
						</div>
					</div>
				) : null}

				<div className="rounded-2xl border border-border bg-muted/50 p-4">
					<div className="flex items-center justify-between text-xs text-muted-foreground">
						<span>You pay</span>
						<button
							type="button"
							className="min-h-touch font-semibold text-primary"
							onClick={() =>
								setAmount(
									maxSpendable > 0
										? formatAmountPlain(maxSpendable, 4)
										: amount,
								)
							}
						>
							Max {maxSpendable > 0 ? formatAmountPlain(maxSpendable, 4) : "—"}
						</button>
					</div>
					<div className="mt-2 flex min-w-0 items-center gap-2">
						<input
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							className="min-w-0 flex-1 bg-transparent font-mono-amount text-2xl font-semibold outline-none"
							inputMode="decimal"
							aria-label="Amount to swap"
						/>
						<TokenSelectSheet
							value={from}
							options={["ckUSDC", "USDM"]}
							onChange={setFrom}
						/>
					</div>
				</div>

				<div className="flex justify-center">
					<button
						type="button"
						aria-label="Flip direction"
						className="inline-flex size-11 items-center justify-center rounded-full border border-border bg-card shadow-sm"
						onClick={flip}
					>
						<ArrowDownUp className="size-4" />
					</button>
				</div>

				<div className="rounded-2xl border border-border bg-muted/30 p-4">
					<p className="text-xs text-muted-foreground">You receive (est.)</p>
					<div className="mt-2 flex items-center justify-between gap-2">
						<p className="font-mono-amount text-2xl font-semibold">
							<AnimatedNumber value={out} decimals={4} />
						</p>
						<TokenSelectSheet
							value={to}
							options={["USDM", "ckUSDC"]}
							onChange={setTo}
						/>
					</div>
				</div>

				<p className="text-[11px] text-muted-foreground">
					First step signs an ICRC-2 approve so the protocol can pull {from}; then
					the swap runs. Fee ({feeBps} bps) goes to treasury surplus.
				</p>

				<RequireAuthAction
					labelWhenUnauthed="Connect to swap"
					disabled={pending}
					onAuthenticated={() => {
						void executeSwap();
					}}
				>
					{pending ? "Swapping…" : `Swap ${from} → ${to}`}
				</RequireAuthAction>
			</div>
		</div>
	);
}
